// The wilds' inhabitants, as the wire knows them: species tokens, posture
// tokens, and the few constants the client needs to decide what a keypress
// was reaching for. Everything else about a creature — vigor, threat
// memory, territory, tuning — is server-only and lives in
// apps/server/src/wilds/; no number about a creature's insides ever
// crosses the boundary.
//
// Species are WORDS and stay words. A creature has no level, no health
// bar, no name tag and no floating anything: you read it the way you read
// an animal — by silhouette, by posture, and by how fast it is coming.

/** Every species the surface simulates. */
export const CREATURE_SPECIES = [
  /** Plains grazer. Big, placid, and gone the moment you press it. */
  "brasshorn",
  /** Ridgeland pack predator. Shrieks, circles, harries. */
  "skirl",
  /** Fen lurker. Holds still in the pools until something warm is close. */
  "sumpjack",
  /** Rogue automaton. Walks a dead company's beat in the deep wilds. */
  "warden",
] as const;

export type CreatureSpecies = (typeof CREATURE_SPECIES)[number];

export function isCreatureSpecies(v: unknown): v is CreatureSpecies {
  return typeof v === "string" && (CREATURE_SPECIES as readonly string[]).includes(v);
}

/** Narrow a wire value; an unknown species reads as a skirl-shaped nothing
 *  rather than a crash (same guard idiom as asCarriage). */
export function asCreatureSpecies(v: unknown): CreatureSpecies {
  return isCreatureSpecies(v) ? v : "skirl";
}

/**
 * How a creature is carrying itself. This is the whole of what a client
 * learns about a creature's disposition, and it is deliberately enough:
 * the renderer keys animation on it, the audio keys cries on it, and a
 * player who can read a low head and a fixed lamp has all the information
 * the server has decided to give anyone.
 */
export const CREATURE_POSTURES = [
  /** Standing, grazing, dormant. Nothing has its attention. */
  "idle",
  /** Moving through its own ground with no particular object. */
  "wander",
  /** It has noticed something and is keeping it in view. */
  "stalk",
  /** Closing. The decision has been made. */
  "press",
  /** The attack itself, held for the strike's duration. */
  "lunge",
  /** Breaking off — driven, spooked, or done with you. */
  "flee",
  /** On the ground, or seized up. It does not get up. */
  "down",
] as const;

export type CreaturePosture = (typeof CREATURE_POSTURES)[number];

export function asCreaturePosture(v: unknown): CreaturePosture {
  return typeof v === "string" && (CREATURE_POSTURES as readonly string[]).includes(v)
    ? (v as CreaturePosture)
    : "idle";
}

/**
 * The label a sentence uses for a species. Player-facing copy — words,
 * never a digit (the same rule every surface obeys).
 */
export function speciesLabel(species: CreatureSpecies): string {
  switch (species) {
    case "brasshorn":
      return "a brasshorn";
    case "skirl":
      return "a skirl";
    case "sumpjack":
      return "a sumpjack";
    case "warden":
      return "a warden";
  }
}

/**
 * How close a strike reaches. Shared because the client uses it to decide
 * whether a keypress was reaching for anything at all (and stays silent
 * otherwise); the server re-validates with its own arc and range and its
 * answer wins, as always.
 */
export const STRIKE_REACH_M = 2.8;

/** The defensive verbs, as the wire spells them. */
export const COMBAT_VERBS = ["strike", "evade", "brace", "steady"] as const;
export type CombatVerb = (typeof COMBAT_VERBS)[number];

export function isCombatVerb(v: unknown): v is CombatVerb {
  return typeof v === "string" && (COMBAT_VERBS as readonly string[]).includes(v);
}
